import React from "react";
import {
  formatINR,
  formatNumber,
  formatPercent,
} from "./reportUtils";

const formatTotalValue = (value, format) => {
  if (value === null || value === undefined) {
    return "—";
  }

  if (format === "inr") {
    return formatINR(value);
  }

  if (format === "percent") {
    return formatPercent(value);
  }

  if (format === "number") {
    return formatNumber(value);
  }

  return value;
};

const ReportTable = ({
  columns = [],
  rows = [],
  totals = {},
  totalLabel = "Total",
  loading = false,
  error = "",
  page = 1,
  totalPages = 1,
  total = 0,
  sortBy = "",
  sortOrder = "asc",
  onSort,
  onPageChange,
  rowKey = "id",
  emptyText = "No records found.",
}) => {
  const handleSort = (column) => {
    if (!column.sortable || !onSort) {
      return;
    }

    const nextOrder =
      sortBy === column.key && sortOrder === "asc"
        ? "desc"
        : "asc";

    onSort(column.key, nextOrder);
  };

  const renderSortIcon = (column) => {
    if (!column.sortable) {
      return null;
    }

    if (sortBy !== column.key) {
      return (
        <span className="report-sort-icon" aria-hidden="true">
          ↕
        </span>
      );
    }

    return (
      <span className="report-sort-icon" aria-hidden="true">
        {sortOrder === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  return (
    <>
      <div className="np-table-wrap">
        <div className="table-responsive report-table-scroll">
          <table className="table table-bordered table-hover align-middle mb-0 np-table">
            <thead className="table-light">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.className || ""}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className="report-sort-button"
                        onClick={() => handleSort(column)}
                        aria-label={`Sort by ${column.label}`}
                      >
                        <span>{column.label}</span>
                        {renderSortIcon(column)}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading &&
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={`loader-${index}`}>
                    {columns.map((column) => (
                      <td key={column.key}>
                        <div className="report-skeleton-line" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!loading && error && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center"
                  >
                    <div className="np-alert">
                      {error}
                    </div>
                  </td>
                </tr>
              )}

              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="text-center"
                  >
                    <div className="np-empty-state">
                      {emptyText}
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                !error &&
                rows.map((row, index) => (
                  <tr key={row[rowKey] || row.id || index}>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={column.className || ""}
                      >
                        {column.render
                          ? column.render(row)
                          : row[column.key] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>

            {!loading && !error && rows.length > 0 && (
              <tfoot>
                <tr className="table-light">
                  {columns.map((column, index) => {
                    if (index === 0) {
                      return (
                        <td
                          key={column.key}
                          className="text-dark fw-bold"
                        >
                          {totalLabel}
                        </td>
                      );
                    }

                    const totalData = totals[column.key];

                    if (!totalData) {
                      return (
                        <td
                          key={column.key}
                          className="text-dark fw-bold"
                        >
                          —
                        </td>
                      );
                    }

                    return (
                      <td
                        key={column.key}
                        className="text-dark fw-bold"
                      >
                        {formatTotalValue(
                          totalData.value,
                          totalData.format,
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  );
};

export default ReportTable;